/**
 * useDealsTable Hook - Server-Side Query Builder
 * 
 * Advanced hook for managing deals table with full server-side:
 * - Filtering (text, numeric, date, select, array, boolean)
 * - Sorting (any column, asc/desc)
 * - Pagination (true LIMIT/OFFSET with total count)
 * - JOINs for contact data and related parties
 * 
 * This replaces client-side filtering/sorting that was necessary with Firebase.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { getSupabase, isSupabaseConfigured, transformDealToCamelCase, transformContactToCamelCase, fetchAllRows } from '../utils/supabase';
import { applyVisibilityScope, visibilityScopeKey } from '../utils/recordVisibility';
import moment from 'moment';
import { FILTER_OPERATORS, RELATIVE_DATE_PRESETS } from './useContactsTable';

// Re-export filter operators from contacts hook for consistency
export { FILTER_OPERATORS, RELATIVE_DATE_PRESETS };

// Map frontend field names to database column names
const FIELD_MAP = {
  dealName: 'deal_name',
  name: 'deal_name',
  stage: 'stage',
  amount: 'amount',
  source: 'source_medium',
  product: 'product',
  loanNumber: 'loan_number',
  lender: 'lender',
  ariveId: 'arive_id',
  propertyType: 'property_type',
  occupancyType: 'occupancy_type',
  loanPurpose: 'loan_purpose',
  purchaseOrRefi: 'purchase_or_refi',
  downPayment: 'down_payment',
  creditScore: 'credit_score',
  interestRate: 'interest_rate',
  loanTerm: 'loan_term',
  tags: 'tags',
  activeCampaigns: 'active_campaign_ids',
  dealTeamRoles: 'deal_team_roles',
  isAtRisk: 'is_at_risk',
  // DNC mirrors (denormalized onto crm_deals from the linked contact)
  dnc: 'dnc',
  dncCall: 'dnc_call',
  dncEmail: 'dnc_email',
  dncSms: 'dnc_sms',
  nextAction: 'next_action',
  nextActionDue: 'next_action_due',
  estimatedClosingDate: 'estimated_closing_date',
  appDate: 'app_date',
  createdAt: 'created_at',
  updatedAt: 'updated_at',
  // Property address fields
  propertyStreet: 'property_street',
  propertyCity: 'property_city',
  propertyState: 'property_state',
  propertyZip: 'property_zip',
  // Contact fields (from JOIN)
  contactName: 'contact.first_name',
  contactEmail: 'contact.email',
  contactPhone: 'contact.phone',
  // Column ID mappings (tableColumnDefinitions uses these IDs)
  created: 'created_at',
  lastTouched: 'updated_at',
  due: 'next_action_due',
  estClosingDate: 'estimated_closing_date',
  atRisk: 'is_at_risk',
  owner: 'owner_id',
  slaTime: 'created_at',
  propertyAddress: 'property_street',
  recordScope: 'record_scope',
};

// Valid sortable columns in crm_deals
const VALID_SORT_COLUMNS = [
  'deal_name', 'stage', 'amount', 'source_medium', 'product',
  'loan_number', 'lender', 'arive_id', 'property_type', 'occupancy_type',
  'loan_purpose', 'credit_score', 'interest_rate', 'loan_term',
  'is_at_risk', 'next_action', 'next_action_due', 'estimated_closing_date',
  'app_date', 'created_at', 'updated_at', 'property_state', 'property_city',
  'property_street', 'id', 'owner_id', 'contact_id', 'record_scope',
];

/**
 * Get valid database column for sorting, with fallback
 */
function getValidSortColumn(field) {
  const mappedField = FIELD_MAP[field] || field;
  // Check if the mapped field is valid (and not a JOIN field)
  if (VALID_SORT_COLUMNS.includes(mappedField) && !mappedField.includes('.')) {
    return mappedField;
  }
  // Fallback to updated_at if field is invalid or is a JOIN field
  if (mappedField.includes('.')) {
    console.warn(`Cannot sort by joined field "${field}", falling back to updated_at`);
  } else {
    console.warn(`Invalid sort field "${field}" (mapped to "${mappedField}"), falling back to updated_at`);
  }
  return 'updated_at';
}

/**
 * Calculate date range from relative preset
 */
function getRelativeDateRange(preset) {
  const now = moment();
  
  switch (preset) {
    case 'today':
      return { start: now.startOf('day').toISOString(), end: now.endOf('day').toISOString() };
    case 'yesterday':
      return { 
        start: moment().subtract(1, 'day').startOf('day').toISOString(), 
        end: moment().subtract(1, 'day').endOf('day').toISOString() 
      };
    case 'last_7_days':
      return { start: moment().subtract(7, 'days').startOf('day').toISOString(), end: moment().endOf('day').toISOString() };
    case 'last_14_days':
      return { start: moment().subtract(14, 'days').startOf('day').toISOString(), end: moment().endOf('day').toISOString() };
    case 'last_30_days':
      return { start: moment().subtract(30, 'days').startOf('day').toISOString(), end: moment().endOf('day').toISOString() };
    case 'last_90_days':
      return { start: moment().subtract(90, 'days').startOf('day').toISOString(), end: moment().endOf('day').toISOString() };
    case 'this_month':
      return { start: moment().startOf('month').toISOString(), end: moment().endOf('month').toISOString() };
    case 'last_month':
      return { 
        start: moment().subtract(1, 'month').startOf('month').toISOString(), 
        end: moment().subtract(1, 'month').endOf('month').toISOString() 
      };
    case 'this_year':
      return { start: moment().startOf('year').toISOString(), end: moment().endOf('year').toISOString() };
    case 'closing_this_week':
      return { start: moment().startOf('week').toISOString(), end: moment().endOf('week').toISOString() };
    case 'closing_this_month':
      return { start: moment().startOf('month').toISOString(), end: moment().endOf('month').toISOString() };
    default:
      return null;
  }
}

// Array columns where "empty" means either SQL NULL or an empty array — both
// exist in crm_deals (~2,000 NULL vs ~19 empty), so both must match.
// (active_campaign_ids is NOT NULL DEFAULT '{}', but the NULL branch is
// harmless and keeps the set uniform.)
const ARRAY_FIELDS = new Set(['tags', 'active_campaign_ids', 'deal_team_roles']);

// Collapse a burst of realtime row changes into one refetch.
const REALTIME_REFETCH_DEBOUNCE_MS = 500;

/**
 * Quote one element of a Postgres array literal.
 *
 * supabase-js builds array filters by joining values raw (`ov.{a,b,c}`), so a
 * value containing a comma is silently split into two values, and one with
 * leading/trailing whitespace never matches because the array parser trims
 * unquoted elements. Double-quoting each element makes both cases exact.
 */
function quoteArrayElement(value) {
  return `"${String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
}

/**
 * Build a fully quoted Postgres array literal, e.g. {"a","b, still b"}
 */
function toArrayLiteral(values) {
  return `{${values.map(quoteArrayElement).join(',')}}`;
}

/**
 * Quote one element of a PostgREST `in.(...)` list.
 */
function quoteInElement(value) {
  return `"${String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
}

/**
 * Match rows where an array column holds no values (NULL or empty array).
 */
function applyArrayIsEmpty(query, dbField) {
  return query.or(`${dbField}.is.null,${dbField}.eq.{}`);
}

/**
 * Apply a single filter condition to a Supabase query.
 *
 * Exported so other query paths (e.g. the MyDealsPage stat cards) can reuse the
 * exact same filter chain and never disagree with the table.
 */
export function applyFilterToQuery(query, filter) {
  const { field, operator, value } = filter;
  const dbField = FIELD_MAP[field] || field;
  
  // Handle empty/null checks first
  if (operator === FILTER_OPERATORS.is_empty) {
    // On an array column, "is empty" must also match the empty-array rows
    return ARRAY_FIELDS.has(dbField)
      ? applyArrayIsEmpty(query, dbField)
      : query.is(dbField, null);
  }
  if (operator === FILTER_OPERATORS.is_not_empty) {
    // On an array column, an empty array {} is stored data but not a value,
    // so "is not empty" must reject it along with NULL.
    return ARRAY_FIELDS.has(dbField)
      ? query.not(dbField, 'is', null).not(dbField, 'eq', '{}')
      : query.not(dbField, 'is', null);
  }
  if (operator === FILTER_OPERATORS.array_is_empty) {
    return applyArrayIsEmpty(query, dbField);
  }
  
  // Boolean operators take no value input, so they must run before the
  // no-value skip below. The FilterBuilder leaves value as '' for them, which
  // previously made every advanced-builder boolean filter (DNC "Is Yes/No",
  // At Risk, ...) a silent no-op.
  if (operator === FILTER_OPERATORS.is_true) {
    return query.eq(dbField, true);
  }
  if (operator === FILTER_OPERATORS.is_false) {
    // NULL-safe: an unset flag (NULL) counts as "No", so exclusion filters
    // like "DNC: SMS is No" keep rows where the flag was never written.
    return query.or(`${dbField}.is.null,${dbField}.eq.false`);
  }
  
  // Skip if no value provided (except for the operators above)
  if (value === undefined || value === null || value === '') {
    return query;
  }
  
  // Text operators
  if (operator === FILTER_OPERATORS.contains) {
    return query.ilike(dbField, `%${value}%`);
  }
  if (operator === FILTER_OPERATORS.equals) {
    return query.eq(dbField, value);
  }
  if (operator === FILTER_OPERATORS.starts_with) {
    return query.ilike(dbField, `${value}%`);
  }
  if (operator === FILTER_OPERATORS.ends_with) {
    return query.ilike(dbField, `%${value}`);
  }
  
  // Numeric operators
  if (operator === FILTER_OPERATORS.eq) {
    return query.eq(dbField, value);
  }
  if (operator === FILTER_OPERATORS.gt) {
    return query.gt(dbField, value);
  }
  if (operator === FILTER_OPERATORS.lt) {
    return query.lt(dbField, value);
  }
  if (operator === FILTER_OPERATORS.gte) {
    return query.gte(dbField, value);
  }
  if (operator === FILTER_OPERATORS.lte) {
    return query.lte(dbField, value);
  }
  if (operator === FILTER_OPERATORS.between && Array.isArray(value) && value.length === 2) {
    return query.gte(dbField, value[0]).lte(dbField, value[1]);
  }
  
  // Date operators
  if (operator === FILTER_OPERATORS.date_eq) {
    const startOfDay = moment(value).startOf('day').toISOString();
    const endOfDay = moment(value).endOf('day').toISOString();
    return query.gte(dbField, startOfDay).lte(dbField, endOfDay);
  }
  if (operator === FILTER_OPERATORS.date_before) {
    return query.lt(dbField, moment(value).startOf('day').toISOString());
  }
  if (operator === FILTER_OPERATORS.date_after) {
    return query.gt(dbField, moment(value).endOf('day').toISOString());
  }
  if (operator === FILTER_OPERATORS.date_between && Array.isArray(value) && value.length === 2) {
    return query.gte(dbField, moment(value[0]).startOf('day').toISOString())
                .lte(dbField, moment(value[1]).endOf('day').toISOString());
  }
  if (operator === FILTER_OPERATORS.date_relative) {
    const range = getRelativeDateRange(value);
    if (range) {
      return query.gte(dbField, range.start).lte(dbField, range.end);
    }
  }
  
  // Select operators (for enum/dropdown fields)
  if (operator === FILTER_OPERATORS.in && Array.isArray(value) && value.length > 0) {
    return query.in(dbField, value);
  }
  if (operator === FILTER_OPERATORS.not_in && Array.isArray(value) && value.length > 0) {
    return query.not(dbField, 'in', `(${value.map(quoteInElement).join(',')})`);
  }
  
  // Array operators (for tags, etc.). These use .filter() with a hand-built
  // literal rather than .overlaps()/.contains(), which join values unquoted.
  if (operator === FILTER_OPERATORS.contains_any && Array.isArray(value) && value.length > 0) {
    return query.filter(dbField, 'ov', toArrayLiteral(value));
  }
  if (operator === FILTER_OPERATORS.contains_all && Array.isArray(value) && value.length > 0) {
    return query.filter(dbField, 'cs', toArrayLiteral(value));
  }
  if (operator === FILTER_OPERATORS.not_contains_any && Array.isArray(value) && value.length > 0) {
    // Exclude rows overlapping any of the values. NOT(ov) alone would also
    // drop NULL-array rows (NOT NULL is NULL), but a deal with no tags
    // definitionally doesn't have an excluded tag, so keep them via the OR.
    return query.or(`${dbField}.is.null,${dbField}.not.ov.${toArrayLiteral(value)}`);
  }
  
  return query;
}

// Global search caps how many matching contacts it will pull in, because their
// ids are inlined into the deals query. Deals whose contact falls outside this
// window are not matched by contact fields (deal_name/loan_number still match).
const CONTACT_SEARCH_LIMIT = 500;

/**
 * Search contacts and return matching contact IDs, for global search across
 * contact fields (first name, last name, email, phone).
 *
 * Phone search is format-agnostic: strips non-digit characters from the search
 * term so "(904) 555-1234", "904-555-1234", and "9045551234" all find the same
 * contacts regardless of how the phone was stored.
 */
export async function searchContactIdsForTerm(term) {
  if (!term || term.trim().length < 2 || !isSupabaseConfigured()) return [];

  const supabase = getSupabase();
  const trimmed = term.trim();
  const pattern = `%${trimmed}%`;

  // Build OR conditions for name and email (always use the raw term)
  const orConditions = [
    `first_name.ilike.${pattern}`,
    `last_name.ilike.${pattern}`,
    `email.ilike.${pattern}`,
  ];

  // For phone: search both the raw term AND a digits-only version
  // so any format works regardless of how the phone is stored
  orConditions.push(`phone.ilike.${pattern}`);

  const digitsOnly = trimmed.replace(/\D/g, '');
  if (digitsOnly.length >= 4 && digitsOnly !== trimmed) {
    // Also search the stripped digits version (e.g. "9045551234" from "(904) 555-1234")
    orConditions.push(`phone.ilike.%${digitsOnly}%`);
  }

  try {
    const { data: matchingContacts, error: contactSearchError } = await supabase
      .from('crm_contacts')
      .select('id')
      // Deterministic order so the capped window is stable between calls,
      // otherwise the same search can return different deals each time.
      .or(orConditions.join(','))
      .order('id', { ascending: true })
      .limit(CONTACT_SEARCH_LIMIT);

    if (contactSearchError) {
      console.error('Error searching contacts:', contactSearchError);
      return [];
    }

    return (matchingContacts || []).map(c => c.id);
  } catch (err) {
    console.error('Error in searchContactIdsForTerm:', err);
    return [];
  }
}

/**
 * Apply owner, stage, global search and all filter conditions to a deals query.
 *
 * Every deals query path (the table page, Select All, CSV export, and the
 * MyDealsPage stat cards) funnels through here so they can never disagree about
 * what the current filters mean.
 *
 * Synchronous by design — a query builder is thenable and must never be
 * returned from an async function (see resolveSearchContactIds). Callers pass
 * the already-resolved `contactIds` for global search.
 *
 * @param {Object} query - Supabase query builder over crm_deals
 * @param {Object} scope
 * @param {Object|null} scope.ownerScope - Multi-owner visibility scope (from
 *   useVisibilityScope). Wins over ownerId when provided.
 * @param {string|null} scope.ownerId - Legacy: restrict to one owner (null = all users)
 * @param {string|string[]|null} scope.stageFilter - Quick stage filter
 * @param {string} scope.searchTerm - Global search term
 * @param {Array} scope.filters - Filter conditions from the toolbar/builder
 * @param {string[]} scope.contactIds - Contact ids matching the search term
 * @returns {Object} The query with all scope applied
 */
export function applyDealScopeSync(query, {
  ownerScope = null,
  ownerId = null,
  stageFilter = null,
  searchTerm = '',
  filters = [],
  contactIds = [],
} = {}) {
  // Visibility: multi-owner scope, legacy owner filter, or admin all-users
  if (ownerScope) {
    query = applyVisibilityScope(query, ownerScope);
  } else if (ownerId) {
    query = query.eq('owner_id', ownerId);
  }

  // Apply stage quick filter if provided
  if (stageFilter) {
    if (Array.isArray(stageFilter)) {
      query = query.in('stage', stageFilter);
    } else {
      query = query.eq('stage', stageFilter);
    }
  }

  // Apply global search across deal fields AND contact fields
  if (searchTerm && searchTerm.trim().length >= 2) {
    const term = `%${searchTerm.trim()}%`;

    if (contactIds.length > 0) {
      // Match deals by deal_name/loan_number/arive_id OR by linked contact
      query = query.or(`deal_name.ilike.${term},loan_number.ilike.${term},arive_id.ilike.${term},contact_id.in.(${contactIds.join(',')})`);
    } else {
      // No matching contacts, just search deal fields
      query = query.or(`deal_name.ilike.${term},loan_number.ilike.${term},arive_id.ilike.${term}`);
    }
  }

  // Apply all filter conditions
  for (const filter of filters) {
    query = applyFilterToQuery(query, filter);
  }

  return query;
}

/**
 * Resolve the contact ids a global search needs, ready to hand to
 * applyDealScopeSync. Returns [] when the term is too short to search.
 *
 * Deliberately returns ids rather than a query: a Supabase query builder is
 * thenable, so returning one from an async function makes the promise machinery
 * adopt it, execute the request, and resolve with a `{ data, error, count }`
 * result object instead of the builder. Any `.order()`/`.range()` chained
 * afterwards then fails with "query.order is not a function". Keep every
 * builder mutation synchronous and await only plain data.
 */
export async function resolveSearchContactIds(searchTerm) {
  if (!searchTerm || searchTerm.trim().length < 2) return [];
  return searchContactIdsForTerm(searchTerm);
}

// Realtime: one filtered channel per accessible owner up to this cap; beyond
// it (or for admin "all") a single unfiltered channel is used instead.
const MAX_REALTIME_OWNER_CHANNELS = 10;

/**
 * Main hook for deals table with server-side operations
 *
 * Visibility: pass `ownerScope` (from useVisibilityScope) for the multi-owner
 * model — owned records, team book-shares, record-level shares, and company
 * records. The legacy `ownerId` param still works as a plain owner_id equality
 * filter for callers that have not migrated; `ownerScope` wins when both are
 * provided.
 */
export function useDealsTable({
  ownerId,
  ownerScope = null,
  pageSize = 25,
  initialFilters = [],
  initialSort = { field: 'updatedAt', direction: 'desc' },
  initialPage = 1,
  globalSearchTerm = '',
  stageFilter = null, // Quick filter for single stage
  includeParticipants = true, // Whether to fetch related parties
} = {}) {
  // State
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [currentPageSize, setCurrentPageSize] = useState(pageSize);
  const [filters, setFilters] = useState(initialFilters);
  const [sort, setSort] = useState(initialSort);
  const [searchTerm, setSearchTerm] = useState(globalSearchTerm);
  
  // Refs
  const subscriptionRef = useRef(null);
  const abortControllerRef = useRef(null);
  // Monotonic request counter: only the newest fetch may commit state, so a
  // slow earlier response can never overwrite a newer one.
  const requestIdRef = useRef(0);

  // Stable key for the active visibility scope — refetch/resubscribe when the
  // accessible owners or record-level grants change.
  const ownerScopeKey = useMemo(() => {
    if (ownerScope) {
      return `${visibilityScopeKey(ownerScope)}:g${ownerScope.grantsVersion || 0}`;
    }
    return ownerId ? `legacy:${ownerId}` : 'all';
  }, [ownerScope, ownerId]);
  
  // Derived values
  const totalPages = useMemo(() => Math.ceil(totalCount / currentPageSize), [totalCount, currentPageSize]);
  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;
  
  /**
   * Fetch participants (related parties) for a list of deals
   */
  const fetchParticipantsForDeals = useCallback(async (dealIds) => {
    if (!dealIds || dealIds.length === 0) return new Map();
    if (!isSupabaseConfigured()) return new Map();
    
    const supabase = getSupabase();
    
    try {
      const { data: participants, error: participantsError } = await supabase
        .from('crm_deal_participants')
        .select(`
          id,
          deal_id,
          role,
          is_primary,
          created_at,
          contact:crm_contacts(
            id,
            first_name,
            last_name,
            email,
            phone,
            contact_types
          )
        `)
        .in('deal_id', dealIds);
      
      if (participantsError) {
        console.error('Error fetching participants:', participantsError);
        return new Map();
      }
      
      // Group by deal_id
      const participantsByDeal = new Map();
      
      (participants || []).forEach(p => {
        const dealId = p.deal_id;
        if (!participantsByDeal.has(dealId)) {
          participantsByDeal.set(dealId, []);
        }
        
        participantsByDeal.get(dealId).push({
          id: p.id,
          contactId: p.contact?.id,
          contactName: p.contact ? `${p.contact.first_name || ''} ${p.contact.last_name || ''}`.trim() : '',
          contactEmail: p.contact?.email,
          contactPhone: p.contact?.phone,
          role: p.role,
          isPrimary: p.is_primary,
          addedAt: p.created_at,
          contact: p.contact ? transformContactToCamelCase(p.contact) : null,
        });
      });
      
      return participantsByDeal;
    } catch (err) {
      console.error('Error in fetchParticipantsForDeals:', err);
      return new Map();
    }
  }, []);

  /**
   * Enrich deals with task information from crm_tasks table.
   * 
   * Fetches pending/in-progress tasks for all deals in a single query,
   * then calculates:
   * - earliestTaskDue: Due date of the earliest pending task
   * - earliestTaskTitle: Title of that task
   * - activeTaskCount: Number of pending/in-progress tasks
   * - hasOverdueTask: Whether any task is overdue
   */
  const enrichDealsWithTaskData = useCallback(async (dealsList) => {
    if (!dealsList || dealsList.length === 0 || !isSupabaseConfigured()) {
      return dealsList;
    }

    const supabase = getSupabase();
    const dealIds = dealsList.map(d => d.id);

    try {
      // Fetch all pending/in-progress tasks for these deals in one query
      const { data: tasks, error: taskError } = await supabase
        .from('crm_tasks')
        .select('deal_id, due_date, title, status')
        .in('deal_id', dealIds)
        .in('status', ['pending', 'in-progress'])
        .order('due_date', { ascending: true });

      if (taskError) {
        console.error('[useDealsTable] Error fetching tasks for enrichment:', taskError);
        // Return deals without enrichment on error
        return dealsList.map(deal => ({
          ...deal,
          earliestTaskDue: null,
          earliestTaskTitle: null,
          activeTaskCount: 0,
          hasOverdueTask: false,
        }));
      }

      // Group tasks by deal_id
      const tasksByDeal = new Map();
      (tasks || []).forEach(task => {
        if (!tasksByDeal.has(task.deal_id)) {
          tasksByDeal.set(task.deal_id, []);
        }
        tasksByDeal.get(task.deal_id).push(task);
      });

      const now = new Date();

      // Enrich each deal with task data
      return dealsList.map(deal => {
        const dealTasks = tasksByDeal.get(deal.id) || [];
        const earliestTask = dealTasks[0]; // Already sorted by due_date ascending

        return {
          ...deal,
          earliestTaskDue: earliestTask?.due_date || null,
          earliestTaskTitle: earliestTask?.title || null,
          activeTaskCount: dealTasks.length,
          hasOverdueTask: dealTasks.some(t => t.due_date && new Date(t.due_date) < now),
        };
      });
    } catch (err) {
      console.error('[useDealsTable] Unexpected error in task enrichment:', err);
      // Return deals without enrichment on error
      return dealsList.map(deal => ({
        ...deal,
        earliestTaskDue: null,
        earliestTaskTitle: null,
        activeTaskCount: 0,
        hasOverdueTask: false,
      }));
    }
  }, []);

  const searchContactIds = useCallback((term) => searchContactIdsForTerm(term), []);
  
  /**
   * Fetch deals with current filters, sort, and pagination
   */
  const fetchDeals = useCallback(async () => {
    if (!isSupabaseConfigured()) {
      setError('Supabase is not configured');
      setLoading(false);
      return;
    }
    
    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const myRequestId = ++requestIdRef.current;
    const isStale = () => myRequestId !== requestIdRef.current;
    
    const supabase = getSupabase();
    setLoading(true);
    setError(null);
    
    try {
      // Build query with JOIN to contacts
      let query = supabase
        .from('crm_deals')
        .select(`
          *,
          contact:crm_contacts(
            id,
            first_name,
            last_name,
            email,
            phone,
            contact_types
          )
        `, { count: 'exact' });
      
      // Resolve search ids first, then mutate the builder synchronously — see
      // resolveSearchContactIds for why the builder must never be awaited.
      const contactIds = await resolveSearchContactIds(searchTerm);
      query = applyDealScopeSync(query, { ownerScope, ownerId, stageFilter, searchTerm, filters, contactIds });
      
      // Apply sorting with validation
      const sortField = getValidSortColumn(sort.field);
      query = query.order(sortField, { ascending: sort.direction === 'asc' });
      
      // Apply pagination
      const offset = (currentPage - 1) * currentPageSize;
      query = query.range(offset, offset + currentPageSize - 1);
      
      // Execute query
      const { data, error: queryError, count } = await query.abortSignal(controller.signal);
      
      if (queryError) throw queryError;
      if (isStale()) return;
      
      // Transform deals to camelCase
      let transformedDeals = (data || []).map(deal => {
        const transformed = transformDealToCamelCase(deal);
        // Include the joined contact data
        if (deal.contact) {
          transformed.contact = transformContactToCamelCase(deal.contact);
        }
        return transformed;
      });
      
      // Fetch participants if requested
      if (includeParticipants && transformedDeals.length > 0) {
        const dealIds = transformedDeals.map(d => d.id);
        const participantsMap = await fetchParticipantsForDeals(dealIds);
        
        transformedDeals = transformedDeals.map(deal => ({
          ...deal,
          relatedParties: participantsMap.get(deal.id) || [],
        }));
      }

      // Enrich deals with task data (earliestTaskDue, activeTaskCount, hasOverdueTask)
      if (transformedDeals.length > 0) {
        transformedDeals = await enrichDealsWithTaskData(transformedDeals);
      }
      
      if (isStale()) return;
      setDeals(transformedDeals);
      setTotalCount(count || 0);
      
    } catch (err) {
      // An aborted request was superseded on purpose; surfacing it would show a
      // spurious error while the newer request is still loading.
      const wasAborted = err.name === 'AbortError' || controller.signal.aborted;
      if (!wasAborted && !isStale()) {
        console.error('Error fetching deals:', err);
        setError(err.message);
      }
    } finally {
      if (!isStale()) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerScopeKey, ownerId, currentPageSize, currentPage, filters, sort, searchTerm, stageFilter, includeParticipants, fetchParticipantsForDeals, enrichDealsWithTaskData]);
  
  // Always-current fetch function, so the realtime subscription can call the
  // latest version without being torn down and rebuilt on every filter change.
  const fetchDealsRef = useRef(fetchDeals);
  useEffect(() => {
    fetchDealsRef.current = fetchDeals;
  }, [fetchDeals]);

  /**
   * Set up Supabase Realtime subscriptions.
   *
   * Keyed on the visibility scope: a burst of row changes is debounced into a
   * single refetch instead of one per event.
   *
   * postgres_changes filters cannot express OR / array-contains, so a scoped
   * view subscribes one owner_id=eq channel per accessible owner (book shares
   * are few). Edits to records that are only visible via a record-level share
   * (shared_with_ids) are not captured live — grant/revoke changes ARE (via
   * useVisibilityScope's crm_record_shares subscription), and normal refetches
   * cover row edits.
   */
  useEffect(() => {
    if (!isSupabaseConfigured()) return;
    
    const supabase = getSupabase();
    
    // Which owners to watch: scope owners, legacy single owner, or everything.
    let watchedOwnerIds = null; // null = unfiltered (admin all-users)
    if (ownerScope && ownerScope.mode === 'scoped') {
      watchedOwnerIds = ownerScope.ownerIds || [ownerScope.targetUid];
    } else if (!ownerScope && ownerId) {
      watchedOwnerIds = [ownerId];
    }
    // Too many owners → fall back to one unfiltered channel.
    if (watchedOwnerIds && watchedOwnerIds.length > MAX_REALTIME_OWNER_CHANNELS) {
      watchedOwnerIds = null;
    }
    
    let debounceTimer = null;
    const onRowChange = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => fetchDealsRef.current(), REALTIME_REFETCH_DEBOUNCE_MS);
    };
    
    const channelSuffix = Math.random().toString(36).slice(2, 8);
    const channels = [];
    if (watchedOwnerIds === null) {
      channels.push(
        supabase
          .channel(`deals-table-changes-all-${channelSuffix}`)
          .on('postgres_changes', { event: '*', schema: 'public', table: 'crm_deals' }, onRowChange)
          .subscribe()
      );
    } else {
      watchedOwnerIds.forEach((watchedOwnerId) => {
        channels.push(
          supabase
            .channel(`deals-table-changes-${watchedOwnerId}-${channelSuffix}`)
            .on(
              'postgres_changes',
              { event: '*', schema: 'public', table: 'crm_deals', filter: `owner_id=eq.${watchedOwnerId}` },
              onRowChange
            )
            .subscribe()
        );
      });
    }
    
    subscriptionRef.current = channels;
    
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      channels.forEach((channel) => supabase.removeChannel(channel));
      subscriptionRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerScopeKey]);
  
  // Fetch on mount and when dependencies change
  useEffect(() => {
    fetchDeals();
  }, [fetchDeals]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);
  
  /**
   * Apply new filters (replaces all existing filters)
   */
  const applyFilters = useCallback((newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  }, []);
  
  /**
   * Add a single filter
   */
  const addFilter = useCallback((filter) => {
    setFilters(prev => [...prev, filter]);
    setCurrentPage(1);
  }, []);
  
  /**
   * Remove a filter by index
   */
  const removeFilter = useCallback((index) => {
    setFilters(prev => prev.filter((_, i) => i !== index));
    setCurrentPage(1);
  }, []);
  
  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    setFilters([]);
    setCurrentPage(1);
  }, []);
  
  /**
   * Apply sorting
   */
  const applySort = useCallback((field, direction = 'asc') => {
    setSort({ field, direction });
    setCurrentPage(1);
  }, []);
  
  /**
   * Toggle sort direction for a field
   */
  const toggleSort = useCallback((field) => {
    setSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  }, []);
  
  /**
   * Set current page
   */
  const setPage = useCallback((page) => {
    const validPage = Math.max(1, Math.min(page, totalPages || 1));
    setCurrentPage(validPage);
  }, [totalPages]);
  
  /**
   * Go to next page
   */
  const nextPage = useCallback(() => {
    if (hasNextPage) {
      setCurrentPage(prev => prev + 1);
    }
  }, [hasNextPage]);
  
  /**
   * Go to previous page
   */
  const prevPage = useCallback(() => {
    if (hasPrevPage) {
      setCurrentPage(prev => prev - 1);
    }
  }, [hasPrevPage]);
  
  /**
   * Update global search term
   */
  const setGlobalSearch = useCallback((term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  }, []);
  
  /**
   * Update page size
   */
  const setPageSize = useCallback((newSize) => {
    setCurrentPageSize(newSize);
    setCurrentPage(1); // Reset to first page when page size changes
  }, []);
  
  /**
   * Refresh data
   */
  const refresh = useCallback(() => {
    fetchDeals();
  }, [fetchDeals]);
  
  /**
   * Fetch all deal IDs matching current filters (for "Select All" functionality)
   * Uses pagination to bypass Supabase's 1000-row limit.
   * Returns an array of deal IDs.
   */
  const fetchAllIds = useCallback(async () => {
    if (!isSupabaseConfigured()) {
      console.error('Supabase is not configured');
      return [];
    }
    
    try {
      // Pre-fetch matching contact IDs for search (async, before the sync callback)
      let matchingContactIds = [];
      if (searchTerm && searchTerm.trim().length >= 2) {
        matchingContactIds = await searchContactIds(searchTerm);
      }
      
      // Use fetchAllRows helper with the shared scope builder
      const rows = await fetchAllRows('crm_deals', 'id', (query) => applyDealScopeSync(query, {
        ownerScope, ownerId, stageFilter, searchTerm, filters, contactIds: matchingContactIds,
      }));
      
      return rows.map(row => row.id);
    } catch (err) {
      console.error('Error fetching all deal IDs:', err);
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerScopeKey, ownerId, stageFilter, filters, searchTerm, searchContactIds]);

  /**
   * Fetch ALL deal rows matching current filters (for CSV export).
   * Like fetchAllIds but selects full columns with contact JOIN.
   * Skips participant/task enrichment for performance on large datasets.
   */
  const fetchAllDeals = useCallback(async () => {
    if (!isSupabaseConfigured()) {
      console.error('Supabase is not configured');
      return [];
    }

    try {
      let matchingContactIds = [];
      if (searchTerm && searchTerm.trim().length >= 2) {
        matchingContactIds = await searchContactIds(searchTerm);
      }

      const selectFields = '*, contact:crm_contacts(*)';

      const rows = await fetchAllRows('crm_deals', selectFields, (query) => applyDealScopeSync(query, {
        ownerScope, ownerId, stageFilter, searchTerm, filters, contactIds: matchingContactIds,
      }));

      return rows.map(deal => {
        const transformed = transformDealToCamelCase(deal);
        if (deal.contact) {
          transformed.contact = transformContactToCamelCase(deal.contact);
        }
        return transformed;
      });
    } catch (err) {
      console.error('Error fetching all deals for export:', err);
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerScopeKey, ownerId, stageFilter, filters, searchTerm, searchContactIds]);
  
  // Group deals by stage for Kanban view
  const dealsByStage = useMemo(() => {
    const grouped = {};
    deals.forEach(deal => {
      const stage = deal.stage || 'unknown';
      if (!grouped[stage]) {
        grouped[stage] = [];
      }
      grouped[stage].push(deal);
    });
    return grouped;
  }, [deals]);
  
  return {
    // Data
    deals,
    dealsByStage,
    totalCount,
    totalPages,
    currentPage,
    pageSize: currentPageSize,
    
    // Status
    loading,
    error,
    hasNextPage,
    hasPrevPage,
    
    // Current state
    filters,
    sort,
    searchTerm,
    
    // Filter actions
    applyFilters,
    addFilter,
    removeFilter,
    clearFilters,
    
    // Sort actions
    applySort,
    toggleSort,
    
    // Pagination actions
    setPage,
    setPageSize,
    nextPage,
    prevPage,
    
    // Search actions
    setGlobalSearch,
    
    // Other actions
    refresh,
    fetchAllIds,
    fetchAllDeals,
  };
}

export default useDealsTable;
